import type * as hast from 'hast';
import { SKIP, visit } from 'unist-util-visit';

import { VerticalAlignment } from '@gitbook/api-client';
import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_EMBED,
    BLOCK_FILE,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    embed,
    emoji,
    FRAGMENT_CAPTION,
    INLINE_EMOJI,
    integration,
    openapiOperation,
    openapiSchemas,
    openapiWebhook,
    paragraph,
    parseLength,
} from '@gitbook/doc-core';
import { Block, Fragment, Text } from '@gitbook/slate';
import { isUrl } from '@gitbook/util-url';

import { transformBlockSwaggerCustoms } from './transform-block-customs-swagger';
import type { HastToGastTransformFn, HastToGastTransformMap } from './types';
import { getCustomAttributes, getEnumAttribute, sanitizeSpaces, trimWhitespace } from './utils';

/**
 * Custom blocks to transform.
 */
export const transformBlockCustoms: HastToGastTransformMap<hast.Element> = {
    ...transformBlockSwaggerCustoms,

    [BLOCK_OPENAPI_OPERATION]: async (node, _eat, _context) => {
        const attributes = getCustomAttributes(node);

        if (
            typeof attributes.spec === 'string' &&
            typeof attributes.path === 'string' &&
            typeof attributes.method === 'string'
        ) {
            return [
                openapiOperation.createBlockWithRef({
                    ref: { kind: 'openapi', spec: attributes.spec },
                    path: attributes.path,
                    method: attributes.method,
                }),
            ];
        }

        return [];
    },

    [BLOCK_OPENAPI_SCHEMAS]: async (node, _eat, _context) => {
        const attributes = getCustomAttributes(node);

        if (typeof attributes.spec === 'string') {
            let schemas: string[] = [];

            if (typeof attributes.schemas === 'string') {
                schemas = attributes.schemas.split(',').map((model) => model.trim());
            }

            const grouped =
                typeof attributes.grouped === 'string' ? attributes.grouped !== 'false' : undefined;

            return [
                openapiSchemas.createOpenAPISchemasBlockWithRef({
                    ref: { kind: 'openapi', spec: attributes.spec },
                    schemas,
                    grouped,
                }),
            ];
        }

        return [];
    },

    [BLOCK_OPENAPI_WEBHOOK]: async (node, _eat, _context) => {
        const attributes = getCustomAttributes(node);

        if (
            typeof attributes.spec === 'string' &&
            typeof attributes.name === 'string' &&
            typeof attributes.method === 'string'
        ) {
            return [
                openapiWebhook.createOpenAPIWebhookBlockWithRef({
                    ref: { kind: 'openapi', spec: attributes.spec },
                    name: attributes.name,
                    method: attributes.method,
                }),
            ];
        }

        return [];
    },

    /**
     * Transform custom code blocks. These contain within them regular Markdown
     * code blocks, but with extra information (such as title etc.)
     */
    [BLOCK_CODE]: async (node, eat, context) => {
        const attributes = getCustomAttributes(node);
        const children = await eat('blocks', node.children, context);

        // The inner code block may be missing, in which case just render an empty code block.
        const code = children.find((node) => node.type === BLOCK_CODE);

        const nodes = code
            ? code.nodes
            : [
                  Block.create({
                      type: BLOCK_CODE_LINE,
                      nodes: [Text.create()],
                  }),
              ];

        return [
            Block.create({
                type: BLOCK_CODE,
                data: {
                    ...(code ? code.data.toObject() : {}),
                    ...(typeof attributes.title !== 'undefined' ? { title: attributes.title } : {}),
                    ...(typeof attributes.overflow !== 'undefined'
                        ? { overflow: attributes.overflow }
                        : {}),
                    ...(typeof attributes.linenumbers !== 'undefined' ? { lineNumbers: true } : {}),
                    ...(typeof attributes.fullwidth !== 'undefined'
                        ? { fullWidth: attributes.fullwidth !== 'false' }
                        : undefined),
                    ...(typeof attributes.expandable !== 'undefined'
                        ? { expandable: attributes.expandable !== 'false' }
                        : undefined),
                    ...(typeof attributes.collapsedlinecount !== 'undefined' &&
                    !Number.isNaN(attributes.collapsedlinecount) &&
                    Number(attributes.collapsedlinecount) > 0
                        ? { collapsedLineCount: Number(attributes.collapsedlinecount) }
                        : undefined),
                },
                nodes,
            }),
        ];
    },

    [BLOCK_EMBED]: async (node, eat, context) => {
        const attributes = getCustomAttributes(node);
        let caption: Fragment | undefined;
        let url: string;

        if (attributes.data) {
            // Legacy content
            try {
                const parsed = JSON.parse(String(attributes.data));
                url = parsed.url;
            } catch (_err) {
                // We can ignore invalid blocks
                return [];
            }
        } else {
            if (attributes.url) {
                // If the URL is on the custom block level, we probably came from
                // Markdown where the <embed> element doesn't exist
                url = String(attributes.url);

                caption = Fragment.create({
                    fragment: FRAGMENT_CAPTION,
                    nodes: await eat('blocks', node.children, context),
                });
            } else {
                // Otherwise we came from HTML where there'll be an inner embed element
                const innerIframe = node.children.find(
                    (node: hast.Element) => node.tagName === 'iframe'
                );
                if (!innerIframe) {
                    return [];
                }

                // @ts-expect-error
                if (!innerIframe.properties.src) {
                    return [];
                }

                const captionEl = node.children.find(
                    (node: hast.Element) =>
                        node.tagName === 'div' &&
                        (typeof node.properties.className === 'string' ||
                            Array.isArray(node.properties.className)) &&
                        node.properties.className.includes('caption')
                );

                caption = Fragment.create({
                    fragment: FRAGMENT_CAPTION,
                    nodes: captionEl
                        ? // @ts-expect-error
                          await eat('blocks', captionEl.children, context)
                        : [paragraph.createParagraph()],
                });

                // @ts-expect-error
                url = String(innerIframe.properties.src);
            }
        }

        return [
            embed.createEmbedBlock(
                url,
                caption,
                typeof attributes.fullwidth !== 'undefined'
                    ? attributes.fullwidth !== 'false'
                    : undefined
            ),
        ];
    },
    [BLOCK_CONTENT_REF]: async (node, _eat, _context, options) => {
        const { url } = getCustomAttributes(node);

        if (typeof url !== 'string') {
            return [];
        }

        const ref = await options.resolveLink(url);
        if (!ref) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_CONTENT_REF,
                data: { ref },
                nodes: [],
                isVoid: true,
            }),
        ];
    },
    [BLOCK_REUSABLE_CONTENT]: async (node, _eat, _context, options) => {
        const { url } = getCustomAttributes(node);

        if (typeof url !== 'string') {
            return [];
        }

        const ref = await options.resolveLink(url);
        if (!ref) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_REUSABLE_CONTENT,
                data: { ref },
                nodes: [],
                isVoid: true,
            }),
        ];
    },
    [BLOCK_FILE]: async (node, eat, context, options) => {
        const { src, caption } = getCustomAttributes(node);
        if (typeof src !== 'string') {
            return [];
        }

        const ref = await options.resolveFile(src);
        if (!ref) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_FILE,
                data: {
                    ref,
                },
                nodes: [],
                isVoid: true,
                fragments: [
                    Fragment.create({
                        fragment: FRAGMENT_CAPTION,
                        nodes:
                            typeof caption === 'string'
                                ? [paragraph.createParagraph(caption)]
                                : await eat('blocks', node.children, context),
                    }),
                ],
            }),
        ];
    },

    [INLINE_EMOJI]: async (node) => {
        const { code } = getCustomAttributes(node);
        if (typeof code !== 'string') {
            return [];
        }
        return [emoji.createEmoji(code)];
    },

    [BLOCK_HINT]: async (node, eat, context) => {
        const { style } = getCustomAttributes(node);
        const nodes = await eat('blocks', node.children, context);

        if (!nodes.length) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_HINT,
                nodes,
                data: { style },
            }),
        ];
    },
    [BLOCK_IF]: async (node, eat, context) => {
        const { expression } = getCustomAttributes(node);
        const nodes = await eat('blocks', node.children, context);

        if (!nodes.length) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_IF,
                nodes,
                data: { expression },
            }),
        ];
    },
    [BLOCK_COLUMNS]: async (node, eat, context) => {
        const attributes = getCustomAttributes(node);
        const nodes = await eat('blocks', node.children, context);
        if (!nodes.length) {
            return [];
        }
        return [
            Block.create({
                type: BLOCK_COLUMNS,
                data: {
                    ...(typeof attributes.fullwidth !== 'undefined'
                        ? { fullWidth: attributes.fullwidth !== 'false' }
                        : undefined),
                },
                nodes,
            }),
        ];
    },
    [BLOCK_COLUMN]: async (node, eat, context) => {
        const attributes = getCustomAttributes(node);
        const nodes = await eat('blocks', node.children, context);
        return [
            Block.create({
                type: BLOCK_COLUMN,
                data: {
                    ...(typeof attributes.width !== 'undefined'
                        ? { width: parseLength(attributes.width) }
                        : undefined),
                    ...(typeof attributes.valign !== 'undefined'
                        ? {
                              verticalAlignment: getEnumAttribute<VerticalAlignment>(
                                  attributes,
                                  'valign',
                                  [
                                      VerticalAlignment.Top,
                                      VerticalAlignment.Middle,
                                      VerticalAlignment.Bottom,
                                  ],
                                  undefined
                              ),
                          }
                        : undefined),
                },
                nodes: nodes.length ? nodes : [paragraph.createParagraph()],
            }),
        ];
    },
    tabs: async (node, eat, context) => {
        const attributes = getCustomAttributes(node);
        const nodes = await eat('blocks', node.children, context);

        if (!nodes.length) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_TABS,
                data: {
                    ...(typeof attributes.fullwidth !== 'undefined'
                        ? { fullWidth: attributes.fullwidth !== 'false' }
                        : undefined),
                },
                nodes,
            }),
        ];
    },
    tab: async (node, eat, context) => {
        const { title } = getCustomAttributes(node);
        const nodes = await eat('blocks', node.children, context);

        return [
            Block.create({
                type: BLOCK_TABS_ITEM,
                nodes: nodes.length ? nodes : [paragraph.createParagraph()],
                data: { title },
            }),
        ];
    },

    stepper: async (node, eat, context) => {
        const nodes = await eat('blocks', node.children, context);
        if (!nodes.length) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_STEPPER,
                nodes,
            }),
        ];
    },
    step: async (node, eat, context) => {
        const nodes = await eat('blocks', node.children, context);
        return [
            Block.create({
                type: BLOCK_STEPPER_STEP,
                nodes,
            }),
        ];
    },

    /**
     * To be consistent with other static generators, we use {% include %} to include reusable content.
     */
    include: async (node, _eat, _context, options) => {
        const attrs = getCustomAttributes(node);
        const filepath = attrs['0'];

        if (typeof filepath !== 'string') {
            return [];
        }

        const ref = await options.resolveReusableContent?.(filepath);
        if (!ref) {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_REUSABLE_CONTENT,
                data: { ref },
                nodes: [],
                isVoid: true,
            }),
        ];
    },

    /**
     * Deprecated custom block {% youtube %} supported in legacy version.
     */
    youtube: async (node) => {
        const url = getCustomAttributes(node).src || resolveYoutubeUrl(node);

        if (typeof url !== 'string') {
            return [];
        }

        return [
            Block.create({
                type: BLOCK_EMBED,
                data: {
                    url,
                },
                nodes: [],
            }),
        ];
    },

    /**
     * Deprecated block from GBN.
     */
    'page-ref': async (node, eat, context, options) => {
        const { page } = getCustomAttributes(node);
        if (typeof page !== 'string') {
            return [];
        }
        const ref = await options.resolveLink(page);

        return ref
            ? [
                  Block.create({
                      type: BLOCK_CONTENT_REF,
                      isVoid: true,
                      data: {
                          ref,
                      },
                      fragments: [
                          Fragment.create({
                              nodes: await eat('blocks', node.children, context),
                          }),
                      ],
                  }),
              ]
            : [];
    },
};

/**
 * Default handler for a custom block, when the block is potentially an integration one.
 */
export const defaultCustomBlockTransform: HastToGastTransformFn<hast.Element> = async (node) => {
    const dataTag = node.properties?.dataTag;
    if (typeof dataTag !== 'string') {
        return [];
    }

    const parts = dataTag.match(/@(\S+)\/(\S+)/);
    if (!parts) {
        return [];
    }

    const props = getCustomAttributes(node);

    const { fullwidth, ...integrationProps } = props;

    return [
        integration.createIntegrationBlock(
            parts[1],
            parts[2],
            integrationProps,
            undefined,
            typeof fullwidth !== 'undefined' ? fullwidth !== 'false' : undefined
        ),
    ];
};

/**
 * Find the URL of a YouTube block. It's either a text node, or it's been
 * parsed as an href.
 */
function resolveYoutubeUrl(node: hast.Element): string | undefined {
    let url: string | undefined;

    visit(
        node,
        // @ts-expect-error
        (node) => node.type === 'text' || node.tagName === 'a',
        (child) => {
            const sanitized = trimWhitespace(
                // @ts-expect-error
                sanitizeSpaces(child.type === 'text' ? child.value : child.properties.href)
            );

            if (isUrl(sanitized)) {
                url = sanitized;

                // If we've found the URL, skip visiting.
                return SKIP;
            }
        }
    );

    return url;
}
